import { Currency } from '../apiServices/types';

export const formatCurrency = (amount: number): string => {
  return amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

export const formatCurrencyWithSign = (amount: number, isIncome: boolean = false): string => {
  const sign = isIncome ? '+' : '';
  return `${sign}${amount.toFixed(2)}`;
};

export const parseCurrencyAmount = (value: string): number => {
  const cleaned = value.replace(/[^\d.,]/g, '').replace(',', '.');
  return parseFloat(cleaned) || 0;
};

/**
 * Конвертує валюту з string (з бекенду) або number в Currency enum
 */
export const parseCurrency = (currency: number | string | Currency): Currency => {
  if (typeof currency === 'number') {
    return currency as Currency;
  }
  
  if (typeof currency === 'string') {
    // Якщо це число як string (наприклад "0", "1")
    const numValue = parseInt(currency);
    if (!isNaN(numValue)) {
      return numValue as Currency;
    }
    
    // Якщо це назва валюти (наприклад "UAH", "USD")
    const currencyNameMap: Record<string, Currency> = {
      'UAH': Currency.UAH,
      'USD': Currency.USD,
      'EUR': Currency.EUR,
      'GBP': Currency.GBP,
      'PLN': Currency.PLN,
      'JPY': Currency.JPY,
      'CAD': Currency.CAD,
      'AUD': Currency.AUD,
      'CHF': Currency.CHF,
      'CNY': Currency.CNY,
      'RUB': Currency.RUB,
      'CZK': Currency.CZK,
      'DKK': Currency.DKK,
      'HUF': Currency.HUF,
      'RON': Currency.RON,
      'SEK': Currency.SEK,
      'NOK': Currency.NOK,
      'TRY': Currency.TRY,
      'ILS': Currency.ILS,
      'AED': Currency.AED,
      'SAR': Currency.SAR,
      'KZT': Currency.KZT,
      'BYN': Currency.BYN
    };
    
    return currencyNameMap[currency.toUpperCase()] || Currency.UAH;
  }
  
  return Currency.UAH;
};

/**
 * Отримує назву валюти для відображення
 */
export const getCurrencyName = (currency: number | string): string => {
  const currencyMap: Record<number, string> = {
    0: 'UAH',
    1: 'USD',
    2: 'EUR',
    3: 'GBP',
    4: 'PLN',
    5: 'JPY',
    6: 'CAD',
    7: 'AUD',
    8: 'CHF',
    9: 'CNY',
    10: 'RUB',
    11: 'CZK',
    12: 'DKK',
    13: 'HUF',
    14: 'RON',
    15: 'SEK',
    16: 'NOK',
    17: 'TRY',
    18: 'ILS',
    19: 'AED',
    20: 'SAR',
    21: 'KZT',
    22: 'BYN'
  };
  
  const currencyCode = typeof currency === 'string' ? parseInt(currency) : currency;
  return currencyMap[currencyCode] || 'UAH';
};

/**
 * Отримує символ валюти для відображення
 */
export const getCurrencySymbol = (currency: string | number): string => {
  const currencyCode = typeof currency === 'number' ? getCurrencyName(currency) : currency;
  const symbols: Record<string, string> = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'UAH': '₴',
    'PLN': 'zł',
    'RUB': '₽',
    'CNY': '¥',
  };
  return symbols[currencyCode] || currencyCode;
};

/**
 * Форматує валюту з символом
 */
export const formatCurrencyWithSymbol = (amount: number, currency: string = 'UAH'): string => {
  const symbol = getCurrencySymbol(currency);
  const formatted = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  
  // Для USD, EUR, GBP символ перед числом
  if (['USD', 'EUR', 'GBP'].includes(currency)) {
    return `${symbol}${formatted}`;
  }
  
  // Для інших валют символ після числа
  return `${formatted} ${symbol}`;
};
